import React, { useState, useEffect, useRef, useCallback } from "react";
import Flickity from 'flickity';
import Headings from "@project-stories/elements/Headings/Headings";
import {Paragraph} from "@arctheme-components/elements/Paragraph/Paragraph";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import { DosagePopup } from "../DosagePopup"
import { SubscribePopup } from "../SubscribePopup";
import useColoredTextParser from "@project-stories/../hooks/useColoredTextParser";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import helpers from 'frontend/js/helpers';
import 'flickity/css/flickity.css';
import { SecondaryCTA } from "../ListProducts";



export default function ListProductsTabbed({ products, settings }) {
  const [activeTab, setActiveTab] = useState(0);
  const [isDosageOpen, setIsDosageOpen] = useState(false);
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);
  const [dosageGuide, setDosageGuide] = useState(false);
  const [subscriptionItem, setSubscriptionItem] = useState(false);
  const { section } = settings;
  const carouselRefs = useRef([]);
  const flktyRefs = useRef([]);
  const isMobile = useMobileBreakpoint("1024px");
  const product_limit = section?.product_limit;
  settings.show_price = section.product_tile_price_show;
  settings.show_quickadd = section.product_tile_quickadd_show;
  settings.show_tags = section.product_tile_tag_show;
  settings.enableWishlist = section.product_tile_wishlist_show;

  const secondaryCTA = section.secondary_cta_type || SecondaryCTA.DOSAGE;
  const { blocks } = settings;

  useEffect(() => {
    if (blocks && blocks.length > 0) {
      blocks.forEach((block, index) => {
        if (carouselRefs.current[index] && !flktyRefs.current[index]) {
          flktyRefs.current[index] = new Flickity(carouselRefs.current[index], {
            wrapAround: false,
            pageDots: section?.show_indicators,
            prevNextButtons: section?.show_navbuttons,
            contain: false,
            cellAlign: 'left',
            freeScroll: false,
            percentPosition: true,
            draggable: true
          });
          
          // Add resize listener for window size changes
          window.addEventListener('resize', () => {
            if (flktyRefs.current[index]) {
              flktyRefs.current[index].resize();
            }
          });
          
          // Fix height calculation after images load
          const images = carouselRefs.current[index].querySelectorAll('img');
          images.forEach(img => {
            if (img.complete) {
              if (flktyRefs.current[index]) flktyRefs.current[index].resize();
            } else {
              img.addEventListener('load', () => {
                if (flktyRefs.current[index]) flktyRefs.current[index].resize();
              });
            }
          });
        }
      });

    }
  }, [blocks]);
  
  useEffect(() => {
    if (flktyRefs.current[activeTab]) {
      setTimeout(() => {
        flktyRefs.current[activeTab].resize();
      }, 500);
    }
    
  }, [activeTab]);
  
  useEffect(() => {
    // Clean up event listeners on unmount
    return () => {
      flktyRefs.current.forEach((flkty, index) => {
        if (flkty) {
          try {
            window.removeEventListener('resize', () => {
              if (flktyRefs.current[index]) flktyRefs.current[index].resize();
            });
            flkty.destroy();
          } catch (e) {
            console.error("Error cleaning up Flickity:", e);
          }
        }
      });
    };
  }, []);

  useEffect(() => {
    const savedTitle = helpers.getCookie('shopping_for');
    if (savedTitle && blocks) {
      const foundIndex = blocks.findIndex(block => block?.settings.title === savedTitle);
      if (foundIndex !== -1) {
        setActiveTab(foundIndex);
      }
    }
  }, [blocks]);

  
  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
    
    const title = blocks[tabIndex]?.settings.title;
    if (title) {
      helpers.setCookie('shopping_for', title);
    }
    
    // Use multiple resize calls with different timeouts
    if (flktyRefs.current[tabIndex]) {
      flktyRefs.current[tabIndex].resize();
      
      setTimeout(() => {
        flktyRefs.current[tabIndex].resize();
      }, 100);
      
      setTimeout(() => {
        flktyRefs.current[tabIndex].resize();
      }, 300);
    }
  };
  

  const tabStyle = section?.tab_style || 'default';
  const tabPosition = section?.tab_position || 'top';
  const productLayout = section?.product_layout || 'grid';
  const tabAlignment = section?.tab_alignment || 'center';


  const getTabAlignmentClasses = () => {
    switch(tabAlignment) {
      case 'left': return 'justify-start';
      case 'right': return 'justify-end';
      case 'center':
      default: return 'justify-center';
    }
  };


  const getProductItemClasses = () => {
    switch(productLayout) {
      case 'carousel': return 'snap-start shrink-0 w-[80%] sm:w-[45%] lg:w-[23%] pr-4';
      default: return '';
    }
  };

  const renderFormattedTitle = useColoredTextParser({
    title: section?.title,
    accentTitleParts: section?.accent_title_parts,
    accentColor: section?.accent_color,
    headerStyle: section?.header_style || "h2",
    seoStyle: section?.seo_title_font_type,
    HeadingsComponent: Headings,
  });

  const onClickDosage = (productItem) => {
    const dosage = productItem?.dosage || productItem?.dosage_form;
    if(dosage) {
      setDosageGuide(productItem);
      setIsDosageOpen(true);
    }
  };

  const onClickSubscribe = (productItem) => {
    if(productItem) {
      setSubscriptionItem(productItem);
      setIsSubscribeOpen(true);
  };
}
  const renderCarousel = (block, key) => {
;
    if (!block) return null;
    
    return (
      <div
        ref={(el) => {
          carouselRefs.current[key] = el;
          // If already initialized, resize the flickity instance
          if (el && flktyRefs.current[key]) {
            setTimeout(() => flktyRefs.current[key].resize(), 100);
          }
        }}
        className={`flex-col w-full max-w-full carousel slide-fade-in-element ${activeTab === key ? 'block' : 'hidden'}`}
        data-carousel-index={key}
        data-tab-index={key}
      >
        {block.slice(0, product_limit > 0 ? product_limit : undefined).map((productItem, index) => {
          const productKey = `${productItem.handle}-${index}-${settings.section_id}`;
          return (
            <div 
              className={`product-item ${getProductItemClasses()} carousel-cell w-[calc(50%-15px)] lg:w-1/3 px-2 lg:px-[10px]`}
              key={productKey}
              data-product-id={productItem.id}
            >
              <ProductTile
                settings={settings}
                product={productItem}
                type={'slider'}
                siblings={undefined}
                showQuickAdd={false}
                onClickDosage={() => onClickDosage(productItem)}
                onClickSubscribe={() => onClickSubscribe(productItem)}
                showDosageButton={secondaryCTA == SecondaryCTA.DOSAGE && ( productItem?.dosage  || productItem.dosage_form )}
                showSubscribeButton={secondaryCTA == SecondaryCTA.SUBSCRIBE}
                noSecondaryButton={secondaryCTA == SecondaryCTA.NONE}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className={`product-list tabbed-list tab-style-${tabStyle}`}>
        { 
          section?.title || section?.subtext  && <div className="section-top text-center pr-[20px] lg:pl-0">
              {section?.title && renderFormattedTitle()}
              {section?.subtext && (
                <Paragraph 
                  text={section?.subtext} 
                  type={isMobile ? section?.subtext_style_mobile : section?.subtext_style || 'b2'} 
                />
              )}
            </div>
        }
        {tabPosition === 'top' && (
          <div className="flex w-full pr-[20px] lg:pr-0">
            <div className="tabbed-list__tabs flex mt-[16px] gap-2 md:gap-4 border border-black" style={{ justifyContent: tabAlignment }}>
              {blocks.map((block, i) => (
                <button
                  key={`top_tab-${i}`}
                  className={`b3--bold carousel-tab-link ${activeTab === i ? 'selected' : ''}`}
                  onClick={() => handleTabChange(i)}
                >
                  {block?.settings.title}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="tabbed-list__container">
          {/* Render ALL carousels but only show the active one */}
          {blocks.map((block, index) => (
            block.collection?.products ? 
              <div key={`carousel-container-${index}`} style={{ 
                visibility: activeTab === index ? 'visible' : 'hidden',
                position: activeTab === index ? 'static' : 'absolute', 
                opacity: activeTab === index ? 1 : 0,
                zIndex: activeTab === index ? 1 : -1
              }}>
                {renderCarousel(block.collection.products, index)}
              </div>
            : null
          ))}
        </div>

        {tabPosition === 'bottom' && (
          <div className={`tabbed-list__tabs flex mt-6 gap-2 md:gap-4 ${getTabAlignmentClasses()}`}>
            {products.map((product, i) => (
              <button
                key={`bottom_tab-${i}`}
                className={`carousel-tab-link ${activeTab === i ? 'selected' : ''}`}
                onClick={() => handleTabChange(i)}
              >
                {product.settings.title}
              </button>
            ))}
          </div>
        )}
      </div>
      <DosagePopup isOpen={isDosageOpen} setIsOpen={setIsDosageOpen} product={dosageGuide} settings={settings} />
      <SubscribePopup isOpen={ isSubscribeOpen} setIsOpen={setIsSubscribeOpen} product={subscriptionItem} settings={settings} translations={settings.translations} />
     
    </>
  );
}